// src/pages/StartPage.js
//
// Route: '/' — the portal's front door for consumers (partners are
// redirected to /advisor before this ever renders — see App.js's
// StartRoute). Public: anonymous and authenticated visitors both land
// here. See portal_start_page_plan.md for the full reasoning.
//
// Principle: auth state is the wrong thing to organize the portal around;
// product state is the right thing. This page reflects what's been done —
// Start / Resume / Done — it never recommends what to do next. That's a
// deliberate boundary against turning this into a second IntelligencePage
// (the workspace, which stays at /intelligence for past-estimate
// management, profile facts, and the assessment section).
//
// Autónomo is folded into the Calculator card, not a third card. Per
// calculators_reference.md, /calculator and /autonomo-calculator are two
// entry orders into ONE canonical CalculatorSession — is_autonomo is a
// session-level fact (self-employment income present or not), not a
// different product. A visitor who already ran either journey sees one
// Calculator card in its 'done' state, never two.
//
// Anonymous visitors always see both cards in their 'none' state. This is
// correct, not a gap: there's currently no account-creation path other
// than completing a product first, so "anonymous" and "has no done state"
// are the same population (confirmed with John, Sept 2026) — no session-
// id/public-token-based anonymous state tracking was built, deliberately.
//
// Snapshot has only two states (Start / Done) — triage hasn't been
// migrated to the draft/publish model (draft_publish_framework.md), so
// there's no "draft" concept for it here. Calculator has three (Start /
// Resume / Done).

import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useProductMode } from '../hooks/useProductMode'
import { resumeCalculatorDraft } from '../lib/resumeCalculatorDraft'
import { AnonHeader, START_PAGE_CARDS } from '../components/HomeLink'
import posthog from '../lib/posthog'

function fmtEur(n) {
  if (n === null || n === undefined) return null
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
  }).format(n)
}

export default function StartPage() {
  const { user, redirectAfterSignIn } = useAuth()
  const navigate = useNavigate()
  const { isAuthenticated, productSummaries } = useProductMode()

  const triage     = productSummaries.triage
  const calculator = productSummaries.calculator
  const copy       = START_PAGE_CARDS

  async function handleResume() {
    if (!user || !calculator.sessionId) return
    await resumeCalculatorDraft(navigate, user.id, calculator.sessionId)
  }

  return (
    <div className="hub-page">
      {/* AppShell hides its real header entirely for anonymous visitors
          everywhere (showChrome = !!user) — '/' is no exception. This is
          the same AnonHeader every other anonymous-visible page now uses
          (CalculatorPage.js, AutonomoCalculatorPage.js, TriagePage.js), so
          an anonymous visitor gets a consistent wordmark + sign-in
          affordance no matter which of these pages they're on.
          FLAG: `product` on the other three pages is always a real
          journey ('calculator'/'autonomo'/'triage') because SignInModal
          presumably tailors copy to it. StartPage isn't tied to one
          journey, so there's no obviously-correct value here — passing
          null rather than guessing. Please check SignInModal.js's actual
          handling of an unset/null product before shipping this; if it
          assumes a valid key (e.g. for a label lookup), this needs a
          real fallback value or a small SignInModal change instead. */}
      <AnonHeader show={!isAuthenticated} product={null} onSignInSuccess={redirectAfterSignIn} />

      <div className="hub-header">
        <div>
          <div className="hub-eyebrow">HolaSam</div>
          <h1 className="hub-title">Where would you like to start?</h1>
          <p className="hub-subtitle">
            Two free tools — pick whichever fits what you need right now.
          </p>
        </div>
      </div>

      {/* ── Snapshot card ── */}
      <div className="hub-card hub-card-spain start-card">
        <div className="hub-card-left">
          <div className="hub-card-title-row">
            <span>🇪🇸</span>
            <h2 className="hub-card-title">{copy.triage.title}</h2>
            {triage.status === 'done' && (
              <span className="hub-card-badge hub-card-badge-complete">✓ Complete</span>
            )}
          </div>
          <p className="hub-card-desc">{copy.triage.description}</p>
          <ul className="hub-card-features">
            {copy.triage.features.map(f => (
              <li key={f} className="hub-card-feature">
                <span className="hub-card-feature-check">✓</span>{f}
              </li>
            ))}
          </ul>

          {triage.status === 'done' ? (
            <Link to={`/spainsnapshot/results/${triage.publicToken}`} className="btn-primary hub-card-btn">
              {copy.triage.doneLabel}
            </Link>
          ) : (
            <Link
              to="/spainsnapshot"
              className="btn-primary hub-card-btn"
              onClick={() => posthog.capture('triage_started')}
            >
              {copy.triage.startLabel}
            </Link>
          )}
        </div>
      </div>

      {/* ── Calculator card — folds IRPF + Autónomo, see file header ── */}
      <div className="hub-card hub-card-calc start-card">
        <div className="hub-card-left">
          <div className="hub-card-title-row">
            <i className="ti ti-calculator" aria-hidden="true" />
            <h2 className="hub-card-title">{copy.calculator.title}</h2>
            {calculator.status === 'done' && (
              <span className="hub-card-badge hub-card-badge-complete">✓ Complete</span>
            )}
          </div>
          <p className="hub-card-desc">{copy.calculator.description}</p>
          <ul className="hub-card-features">
            {copy.calculator.features.map(f => (
              <li key={f} className="hub-card-feature">
                <span className="hub-card-feature-check">✓</span>{f}
              </li>
            ))}
          </ul>

          {calculator.status === 'done' && (
            <div className="hub-calc-result">
              {calculator.estimateLow > 0 && calculator.estimateHigh > 0 ? (
                <>
                  <div className="hub-calc-result-label">Estimated IRPF range</div>
                  <div className="hub-calc-result-value">
                    {fmtEur(calculator.estimateLow)} – {fmtEur(calculator.estimateHigh)}
                  </div>
                </>
              ) : fmtEur(calculator.irpfGross) ? (
                <>
                  <div className="hub-calc-result-label">Estimated IRPF</div>
                  <div className="hub-calc-result-value">{fmtEur(calculator.irpfGross)}</div>
                </>
              ) : null}
            </div>
          )}

          {calculator.status === 'done' && (
            <Link to={`/calculator/result/${calculator.publicToken}`} className="btn-primary hub-card-btn">
              {copy.calculator.doneLabel}
            </Link>
          )}

          {calculator.status === 'draft' && (
            // Drafts never have a usable public_token (draft_publish_framework.md
            // invariant 4), so this can't be a real <Link> with an href — same
            // fetch-then-navigate pattern as IntelligencePage.js's past-scenarios
            // list, via the shared resumeCalculatorDraft helper. Styled as a link
            // via href="#" + preventDefault, matching HomeLink.js's established
            // reasoning (a <button> here picks up default browser chrome that
            // border:none/background:none doesn't fully clear everywhere).
            <a
              href="#"
              className="btn-primary hub-card-btn"
              onClick={e => { e.preventDefault(); handleResume() }}
            >
              {copy.calculator.resumeLabel}
            </a>
          )}

          {calculator.status === 'none' && (
            <div className="start-card-actions">
              {copy.calculator.startOptions.map(opt => (
                <Link
                  key={opt.to}
                  to={opt.to}
                  className="btn-secondary hub-card-btn"
                  onClick={() => posthog.capture(opt.event)}
                >
                  {opt.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}